import { useState } from 'react';
import { toast } from 'react-toastify';

import axios from 'axios';
import config from '../../config';
import dayjs from 'dayjs';

import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';

import { green } from '@mui/material/colors';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const CreateParcelForm = ({ unitId, property, onParcelCreated, onClose }) => {
    const [formData, setFormData] = useState({
        unit: unitId,
        carrier: '',
        trackingNumber: '',
        description: '',
        deliveryDate: dayjs(),
        status: 'Pending',
        notified: false,
    });
    const [error, setError] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        try {
            const payload = {
                ...formData,
                deliveryDate: formData.deliveryDate?.isValid
                    ? formData.deliveryDate.isValid()
                        ? formData.deliveryDate.toISOString()
                        : null
                    : formData.deliveryDate // fallback if it's already ISO string
            };

            const response = await axios.post(`${config.API_URL}/v2/parcel`, payload, {
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}`, 'Content-Type': 'application/json' }
            });

            if (response.status === 200 || response.status === 201) {
                console.log("Parcel sent.");
                onParcelCreated(response.data);
                onClose(); // Close the modal after submission
                toast.success('Parcel created successfully!');
            } else {
                toast.error('Failed to create the parcel.');
            }
        } catch (error) {
            console.error(error);
            setError('Failed to create the parcel.');
            toast.error('Failed to create the parcel.');
        }
    };

    return (
        <div className="popup-overlay">
            <div className="popup-content">
                <button className="close-popup" onClick={onClose}>
                    <i className="fas fa-times"></i>
                </button>
                <h2 className="text-2xl text-black">Add Parcel</h2>
                <TextField
                    label="Carrier"
                    id="carrier"
                    name="carrier"
                    variant="outlined"
                    fullWidth
                    value={formData.carrier}
                    onChange={handleChange}
                    sx={{ marginBottom: '1rem' }}
                    required
                />
                <TextField
                    label="Tracking Number"
                    id="trackingNumber"
                    name="trackingNumber"
                    variant="outlined"
                    fullWidth
                    value={formData.trackingNumber}
                    onChange={handleChange}
                    sx={{ marginBottom: '1rem' }}
                    required
                />
                <TextField
                    label="Description"
                    name="description"
                    multiline
                    fullWidth
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                    sx={{ marginBottom: '1.2rem' }}
                    required
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Delivery Date"
                        value={formData.deliveryDate ? dayjs(formData.deliveryDate) : null}
                        onChange={(newValue) => {
                            if (!newValue || !newValue.isValid()) {
                                setFormData((prevData) => ({ ...prevData, deliveryDate: '' }));
                                return;
                            }
                            setFormData((prevData) => ({
                                ...prevData,
                                deliveryDate: newValue.toISOString(),
                            }));
                        }}
                        sx={{ width: '100%', marginBottom: '1rem' }}
                        required
                    />
                </LocalizationProvider>
                <Autocomplete
                    name="status"
                    value={formData.status || ''}
                    onChange={(event, newValue) => {
                        if (newValue) {
                            setFormData((prevData) => ({
                                ...prevData,
                                status: newValue,
                            }));
                        }
                    }}
                    options={['Pending', 'Delivered', 'Picked Up', 'Lost', 'Returned']}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Status"
                            variant="outlined"
                            fullWidth
                            required
                        />
                    )}
                    sx={{ marginBottom: '1rem' }}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formData.notified}
                            onChange={(e) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    notified: e.target.checked,
                                }))
                            }
                            sx={{
                                '&.Mui-checked': {
                                    color: green[600],
                                },
                            }}
                        />
                    }
                    label="Notified"
                    sx={{
                        fontSize: '14px',
                        '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                        },
                        marginBottom: '1rem',
                    }}
                />
                <button type="button" className="button" onClick={handleSubmit}>Add Parcel</button>
                {error && <div className="error-message">{error}</div>}
            </div>
        </div>
    );
};

export default CreateParcelForm;
